import Router from 'next/router';
import { useState, useEffect, useRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Button, Typography, Fab, Grid, FormControlLabel, Checkbox } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import dayjs from 'dayjs';
import buddhistEraPlugin from 'dayjs/plugin/buddhistEra';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { useForm, FormProvider, useFormContext, Controller } from 'react-hook-form';
import cpdAPI from 'src/api/cpd';
import { CPDEnrollmentForm, CPDEnrollmentFieldNames, RegulatorType } from 'src/helpers/constants';
import { useMediaQueryDevice, useProfile } from 'src/helpers/use';
import { toFormatCPDEnrollmentStringDate } from 'src/helpers/utils';
import {
  ICPDCourseEnrollment,
  ICPDProfile,
  ICPDCourse,
  CPDRegulatorTypeEnum,
  LicenseRenewalTypeEnum,
  CPDLicenseTypeEnum,
  CPDEnrollmentStatusEnum,
} from 'src/interfaces';
import Cookies from 'universal-cookie';

import { Container, FormErrorText, LinkText, CheckboxLabel, AcceptPolicyBox, useStyles } from './style';

import CpdLicensesConfirmationModal, {
  ConfirmLicenseData,
} from '@components/atoms/dialog/cpdLicensesConfirmationModal';
import LoadingButton from '@components/atoms/loadingButton';
import EnrollmentContentSteps from '@components/molecules/cpdEnrollment/enrollmentContentSteps';

dayjs.extend(customParseFormatPlugin);
dayjs.extend(buddhistEraPlugin);

type Props = {
  open: boolean;
  onClose: () => void;
  step?: number;
  cpdCourseEnrollment: ICPDCourseEnrollment;
  cpdCourse: ICPDCourse;
  isEmailValid: boolean;
  initEnrollmentData: CPDEnrollmentForm | null;
  isBundle?: boolean;
};

const EnrollmentStep = ({
  steps,
  activeStep,
  handleStep,
}: {
  steps: number[];
  activeStep: number;
  handleStep: (step: number) => void;
}) => (
  <Box sx={{ width: '100%', mt: '20px', display: { lg: 'block', xs: 'none' } }}>
    <Stepper activeStep={activeStep - 1}>
      {steps.map((label, index) => (
        <Step
          key={label}
          sx={{
            '&': {
              padding: 0,
            },
            '& .MuiStepLabel-iconContainer': {
              padding: 0,
            },
            '& .Mui-active': {
              fontWeight: 'bold',
            },
            '& .Mui-disabled .MuiSvgIcon-root': {
              color: '#EEEEEE',
            },
            '& .Mui-disabled .MuiSvgIcon-root text': {
              fill: 'black',
              opacity: 0.38,
            },
            '& .MuiStepLabel-root svg': {
              width: '50px',
              height: '50px',
            },
          }}
        >
          <StepButton color="inherit" onClick={() => handleStep(index + 1)} />
        </Step>
      ))}
    </Stepper>
  </Box>
);

type FooterProps = {
  handleBack: () => void;
  activeStep: number;
  steps: number[];
};

const Footer = ({ handleBack, activeStep, steps }: FooterProps) => {
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const { matchesMobile } = useMediaQueryDevice();
  const { t } = useTranslation();
  const errorMessage = errors && errors[CPDEnrollmentFieldNames.isAcceptConsentCPDTermAndPolicy]?.message;

  const classes = useStyles();

  return (
    <>
      {steps.length === activeStep && (
        <AcceptPolicyBox className={errorMessage ? 'error' : ''}>
          <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%', p: { xs: 2 } }}>
            <Grid item xs={12} container justifyContent="center">
              <Controller
                rules={{
                  required: {
                    value: true,
                    message: t('cpd.policy.please_accept_policy'),
                  },
                  validate: (value) => !!value,
                }}
                name={CPDEnrollmentFieldNames.isAcceptConsentCPDTermAndPolicy}
                control={control}
                defaultValue={false}
                render={({ field, fieldState: { error } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        size="small"
                        {...field}
                        sx={{ color: error ? 'errorDark.main' : undefined, alignSelf: 'baseline' }}
                      />
                    }
                    classes={{ label: classes.label }}
                    label={
                      <CheckboxLabel>
                        <span>{t('cpd.policy.part_1')}</span>
                        <LinkText href="/cpd/cpd-privacy-policy" target="_blank" rel="noreferrer">
                          {t('cpd.policy.part_2')}
                        </LinkText>{' '}
                        <span>{t('cpd.policy.part_3')}</span>
                        <LinkText href="/cpd/cpd-terms" target="_blank" rel="noreferrer">
                          {t('cpd.policy.part_4')}
                        </LinkText>
                      </CheckboxLabel>
                    }
                  />
                )}
              />
            </Grid>
            {errorMessage && (
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                alignItems="center"
                sx={{ ml: { xs: 4 }, mr: { xs: 2 }, mt: { xs: 1, md: '-4px', lg: 0 } }}
              >
                <FormErrorText>{errorMessage}</FormErrorText>
              </Grid>
            )}
          </Grid>
        </AcceptPolicyBox>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: steps.length === activeStep ? 'initial' : 'auto',
          bgcolor: '#F5F5F5',
          height: { md: '100px', xs: '70px' },
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container>
          <Stack direction="row" justifyContent="space-between">
            {activeStep === 1 ? (
              <Box />
            ) : (
              <Button className="btn back" variant={matchesMobile ? 'text' : 'outlined'} onClick={handleBack}>
                {t('go_back')}
              </Button>
            )}
            <LoadingButton
              loading={watch(CPDEnrollmentFieldNames.loading)}
              variant="contained"
              className="btn"
              type="submit"
            >
              {steps.length === activeStep ? t('proceed_to_payment') : t('next')}
            </LoadingButton>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

const THREE_STEP = [1, 2, 3];
const FOUR_STEP = [1, 2, 3, 4];

const CPDEnrollment = ({
  open,
  onClose,
  step = 1,
  cpdCourseEnrollment,
  cpdCourse,
  isEmailValid,
  isBundle,
  initEnrollmentData,
}: Props) => {
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const methods = useForm<CPDEnrollmentForm>({
    defaultValues: { prefix: '' },
  });

  const isRequestLicense = cpdCourseEnrollment;

  const steps =
    cpdCourse?.regulator === CPDRegulatorTypeEnum.OIC && cpdCourse?.isRequestLicense ? THREE_STEP : FOUR_STEP;

  const [activeStep, setActiveStep] = useState(step);
  const [isOpenLicensesConfirmModal, setIsOpenLicensesConfirmModal] = useState<boolean>(false);
  const [cpdEnrollmentFormData, setCpdEnrollmentFormData] = useState<CPDEnrollmentForm>();
  const [confirmLicenseDatas, setConfirmLicenseDatas] = useState<ConfirmLicenseData[]>([]);
  const isConfirmLicensesRef = useRef<boolean>(false);

  useEffect(() => {
    if (initEnrollmentData) {
      if (step === 2) {
        setTimeout(() => {
          enqueueSnackbar({ message: t('system_updated_successfully'), severity: 'success' });
        }, 500);
      }
      const isInitStepTwo = step === 2;
      const isInitStepThree = step === 3;
      methods.reset({
        ...initEnrollmentData,
        prefix: ['น.ส.', 'นส.', 'น.ส'].includes(initEnrollmentData.prefix!) ? 'นางสาว' : initEnrollmentData.prefix,
        tsiLicenseType: isInitStepTwo ? '' : initEnrollmentData.tsiLicenseType,
        tsiLicenseNo: isInitStepTwo ? '' : initEnrollmentData.tsiLicenseNo,
        tsiStartDate: isInitStepTwo ? '' : toFormatCPDEnrollmentStringDate(initEnrollmentData.tsiStartDate),
        tsiEndDate: isInitStepTwo ? '' : toFormatCPDEnrollmentStringDate(initEnrollmentData.tsiEndDate),
        oicLifeLicenseNo: isInitStepTwo ? '' : initEnrollmentData.oicLifeLicenseNo,
        oicLifeStartDate: isInitStepTwo ? '' : toFormatCPDEnrollmentStringDate(initEnrollmentData.oicLifeStartDate),
        oicLifeEndDate: isInitStepTwo ? '' : toFormatCPDEnrollmentStringDate(initEnrollmentData.oicLifeEndDate),
        oicNonLifeLicenseNo: isInitStepTwo ? '' : initEnrollmentData.oicNonLifeLicenseNo,
        oicNonLifeStartDate: isInitStepTwo
          ? ''
          : toFormatCPDEnrollmentStringDate(initEnrollmentData.oicNonLifeStartDate),
        oicNonLifeEndDate: isInitStepTwo ? '' : toFormatCPDEnrollmentStringDate(initEnrollmentData.oicNonLifeEndDate),
        roundDate: isInitStepThree ? '' : dayjs(initEnrollmentData.roundDate, 'DD/MM/BBBB').toDate(),
        expiredDate: isInitStepThree ? '' : dayjs(initEnrollmentData.expiredDate, 'DD/MM/BBBB').toDate(),
      });
    }
  }, [initEnrollmentData]);

  useEffect(() => {
    if (step === 3 && steps.length === 3) {
      return setActiveStep(step - 1);
    }
    return setActiveStep(step);
  }, [step]);

  const handleLicensesConfirmation = () => {
    if (!cpdEnrollmentFormData) return;
    isConfirmLicensesRef.current = true;
    setIsOpenLicensesConfirmModal(false);
    handleNext(cpdEnrollmentFormData);
  };

  const openConfirmLicneseModal = (data: CPDEnrollmentForm) => {
    // TODO: refactor to currying function (REF: https://github.com/SkillLane/monorepo/pull/835#discussion_r1229185949)
    const confirmLicenseDatasTemp: ConfirmLicenseData[] = [];
    if (data.isSelectOICLifeLicense && data.oicLifeLicenseNo) {
      confirmLicenseDatasTemp.push({
        regulatorType: RegulatorType.OIC,
        applicantType: cpdCourse.applicantType,
        licenseType: CPDLicenseTypeEnum.LIFE,
        licenseNo: data.oicLifeLicenseNo,
      });
    }

    if (data.isSelectOICNonLifeLicense && data.oicNonLifeLicenseNo) {
      confirmLicenseDatasTemp.push({
        regulatorType: RegulatorType.OIC,
        applicantType: cpdCourse.applicantType,
        licenseType: CPDLicenseTypeEnum.NON_LIFE,
        licenseNo: data.oicNonLifeLicenseNo,
      });
    }

    if (data.tsiLicenseNo) {
      confirmLicenseDatasTemp.push({
        regulatorType: RegulatorType.TSI,
        applicantType: cpdCourse.applicantType,
        licenseNo: data.tsiLicenseNo,
      });
    }

    setConfirmLicenseDatas(confirmLicenseDatasTemp);
    setIsOpenLicensesConfirmModal(true);

    setCpdEnrollmentFormData(data);
  };

  const handleNext = async (data: CPDEnrollmentForm) => {
    const allowedConfirmModal =
      cpdCourse.licenseRenewal === LicenseRenewalTypeEnum.FourthPlusRenewal ||
      cpdCourse.regulator === CPDRegulatorTypeEnum.TSI;

    let availableLicenseAmount = false;

    if (cpdCourse.licenseRenewal === LicenseRenewalTypeEnum.FourthPlusRenewal) {
      availableLicenseAmount =
        cpdCourseEnrollment.licenses.length > 1 ||
        cpdCourseEnrollment.status === CPDEnrollmentStatusEnum.RegulatorUnChecked;
    }

    if (cpdCourse.regulator === CPDRegulatorTypeEnum.TSI) {
      availableLicenseAmount = true;
    }

    if (allowedConfirmModal && activeStep === 2 && !isConfirmLicensesRef.current && availableLicenseAmount) {
      openConfirmLicneseModal(data);
      return;
    }

    if (activeStep === steps.length) {
      methods.setValue('loading', true);

      const response = await cpdAPI.createTemporaryCpdEnrollmentFormData(
        cpdCourse.product.id,
        profile?.id!,
        data,
        cpdCourse.product.product_skus[0].id,
      );
      const cookies = new Cookies();
      cookies.set('cpd_enrollment_form_data', data, { path: '/', maxAge: 60 * 60 * 24 });
      if (response.status === 201) {
        methods.setValue('loading', false);
        Router.push(`/courses/${cpdCourse.id_name}/payment`);
      }
      return;
    }

    if (activeStep === 1) {
      const updatedProfile: ICPDProfile = {
        prefix: (data.prefix === 'other' ? data.inputPrefix : data.prefix) as string,
        firstname: data.firstName,
        lastname: data.lastName,
        mobile: data.mobile,
      };
      cpdCourseEnrollment.profile = updatedProfile;
    }
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    const targetStep = activeStep - 1;
    if (targetStep === 2) {
      isConfirmLicensesRef.current = false;
    }
    setActiveStep(targetStep);
  };

  const handleStep = (enrollStep: number) => {
    setActiveStep(enrollStep);
  };

  useEffect(() => {
    if (!open) {
      methods.clearErrors();
    }
  }, [open]);

  return (
    <>
      <CpdLicensesConfirmationModal
        open={isOpenLicensesConfirmModal}
        onClose={() => setIsOpenLicensesConfirmModal(false)}
        onConfirm={handleLicensesConfirmation}
        licenses={confirmLicenseDatas}
      />

      <Drawer PaperProps={{ style: { height: '100vh' } }} anchor="bottom" open={open} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleNext)}>
            <Stack flexDirection="column" sx={{ minHeight: '100vh' }}>
              <Container>
                <EnrollmentStep steps={steps} activeStep={activeStep} handleStep={handleStep} />
                <Box sx={{ my: { md: '36px', xs: '20px' } }}>
                  <Typography color="primary.main" sx={{ typography: { xs: 'h7', md: 'h4' } }}>
                    {t('step_from_to', { from: activeStep, to: steps.length })}
                  </Typography>
                  <EnrollmentContentSteps
                    totalSteps={steps.length}
                    currentStep={activeStep}
                    cpdCourseEnrollment={cpdCourseEnrollment}
                    cpdCourse={cpdCourse}
                    handleNext={handleNext}
                    isEmailValid={isEmailValid}
                    initEnrollmentData={initEnrollmentData}
                  />
                </Box>
              </Container>
              <Fab
                onClick={onClose}
                sx={{
                  position: 'fixed',
                  right: { xs: 20, md: 30 },
                  top: { xs: 20, md: 30 },
                  height: { xs: '40px', md: '60px' },
                  width: { xs: '40px', md: '60px' },
                  bgcolor: 'common.white',
                  boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.160784)',
                }}
              >
                <CloseIcon sx={{ color: '#BDBDBD', fontSize: { xs: '20px', md: '36px' } }} />
              </Fab>
              <Footer handleBack={handleBack} activeStep={activeStep} steps={steps} />
            </Stack>
          </form>
        </FormProvider>
      </Drawer>
    </>
  );
};

export default CPDEnrollment;
